import React, { createContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash.clonedeep';
import * as PropTypes from 'prop-types';
import { collection, doc, onSnapshot, setDoc, updateDoc, deleteDoc, addDoc, getFirestore } from 'firebase/firestore';
import { useFirebase } from '../firebase/firebase-provider';
import { useDataStore } from './datastore-provider';
import { useHome } from './home-provider';
import { useSnackbar } from 'notistack';

interface ShoppingListStoreContextType {
    userShoppingLists: any[] | null;
    homeShoppingLists: any[] | null;
    addUserList: (list: any) => Promise<unknown>;
    addHomeList: (list: any) => Promise<unknown>;
    saveUserList: (list: any, notifySuccess?: any) => void;
    saveHomeList: (list: any, notifySuccess?: any) => void;
    removeUserList: (list: any) => void;
    removeHomeList: (list: any) => void;
    getUserList: (listId: any) => any;
    getHomeList: (listId: any) => any;
}

const COLLECTION_NAME = 'shoppingLists';
const ShoppingListStore = createContext<ShoppingListStoreContextType | undefined>(undefined);

export const useShoppingListStore = () => {
    const context = React.useContext(ShoppingListStore);
    if (context === undefined) {
        throw new Error('useShoppingListStore must be used within a ShoppingListStoreProvider');
    }
    return context;
};

const SHOPPING_LIST_DEFAULTS = {
    items: [],
};

ShoppingListStoreProvider.propTypes = {
    children: PropTypes.element.isRequired,
};
export function ShoppingListStoreProvider(props: any) {
    const [userShoppingLists, setUserShoppingLists] = useState(null);
    const [homeShoppingLists, setHomeShoppingLists] = useState(null);
    const app = useFirebase();
    const { userDb } = useDataStore();
    const { homeDb } = useHome();
    const { enqueueSnackbar } = useSnackbar();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => fetchShoppingLists(userDb, setUserShoppingLists), [userDb]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => fetchShoppingLists(homeDb, setHomeShoppingLists), [homeDb]);

    function fetchShoppingLists(dbRef: any, setter: any) {
        if (dbRef) {
            const db = getFirestore(app);
            const shoppingListsCollection = collection(dbRef, COLLECTION_NAME);

            const unsubscribe = onSnapshot(
                shoppingListsCollection,
                (querySnapshot: any) => {
                    setter(querySnapshot.docs.map((doc: any) => loadShoppingList(doc.data(), doc.id))
                        .sort(compareLists));
                },
                (error: any) => {
                    console.debug(error);
                    enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                }
            );
            return unsubscribe;
        } else {
            setter(null);
        }
    }

    function compareLists(a: any, b: any) {
        if (!a.created || !b.created) return 0;
        return a.created.seconds > b.created.seconds ? -1 : 1;
    }

    function loadShoppingList(list: any, id: any) {
        return {
            ...list,
            shoppingListId: id,
            items: list.items || [],
        };
    }

    function addUserList(list: any) { return addShoppingList(list, userShoppingLists, userDb); }
    function addHomeList(list: any) { return addShoppingList(list, homeShoppingLists, homeDb); }
    function addShoppingList(shoppingList: any, theLists: any, dbRef: any) {
        return new Promise((resolve, reject) => {
            const newList = { ...cloneDeep(SHOPPING_LIST_DEFAULTS), ...shoppingList, created: new Date() };
            if (dbRef) {
                const shoppingListsCollection = collection(dbRef, COLLECTION_NAME);
                addDoc(shoppingListsCollection, newList)
                    .then((docRef: any) => {
                        resolve(docRef.id);
                        enqueueSnackbar('List Added', { variant: 'success' });
                    })
                    .catch((error: any) => {
                        console.debug(error);
                        enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                        reject(error);
                    });
            }
        });
    }

    function saveUserList(list: any, notifySuccess: any) { saveShoppingList(list, userShoppingLists, userDb, notifySuccess); }
    function saveHomeList(list: any, notifySuccess: any) { saveShoppingList(list, homeShoppingLists, homeDb, notifySuccess); }
    function saveShoppingList(shoppingList: any, theLists: any, dbRef: any, notifySuccess = true) {
        if (dbRef) {
            const shoppingListDoc = doc(dbRef, COLLECTION_NAME, shoppingList.shoppingListId);
            updateDoc(shoppingListDoc, shoppingList)
                .then(() => notifySuccess && enqueueSnackbar('List Saved', { variant: 'success' }))
                .catch((error: any) => {
                    console.debug(error);
                    enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                });
        }
    }

    function removeUserList(list: any) { removeShoppingList(list, userShoppingLists, userDb); }
    function removeHomeList(list: any) { removeShoppingList(list, homeShoppingLists, homeDb); }
    function removeShoppingList(listId: any, theLists: any, dbRef: any) {
        if (dbRef) {
            const undoRemove = () => {
                const deleted = theLists.find((sl: any) => sl.shoppingListId === listId);
                const shoppingListDoc = doc(dbRef, COLLECTION_NAME, listId);
                setDoc(shoppingListDoc, deleted)
                    .then(() => enqueueSnackbar('Action Undone', { variant: 'success' }));
            };
            const shoppingListDoc = doc(dbRef, COLLECTION_NAME, listId);
            deleteDoc(shoppingListDoc)
                .then(() => enqueueSnackbar('List Deleted', { variant: 'success', action: (key) => (
                    <button onClick={() => { undoRemove(); }}>UNDO</button>
                ) }))
                .catch((error: any) => {
                    console.debug(error);
                    enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                });
        }
    }

    function getUserList(listId: any) { return getShoppingList(listId, userShoppingLists); }
    function getHomeList(listId: any) { return getShoppingList(listId, homeShoppingLists); }
    function getShoppingList(listId: any, theLists: any) {
        if (!listId || !theLists) return null;
        const shoppingList = theLists.find((sl: any) => sl.shoppingListId === listId);
        return cloneDeep(shoppingList);
    }

    return (
        <ShoppingListStore.Provider value={{
            userShoppingLists,
            homeShoppingLists,
            addUserList,
            addHomeList,
            saveUserList,
            saveHomeList,
            removeUserList,
            removeHomeList,
            getUserList,
            getHomeList,
        }}
        >
            { props.children }
        </ShoppingListStore.Provider>
    );
}
